import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';
import { integration } from '@gitbook/doc-core';
import {
    Alert,
    CardBody,
    CardCheck,
    CardImage,
    CardImageLayout,
    CardRadio,
    CardSkeleton,
    Description,
    Heading,
    IconTile,
    RadioGroup,
    Step,
    StepControls,
    StepControlsSubmitButton,
} from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    IntegrationIcon,
    IntegrationStepWrapper,
    useInstallIntegration,
} from '../../../../Components';
import { apiQuery } from '../../../../state';
import type { GitSyncInstallationStepProps } from '../types';

/**
 * Git provider configuration step inside a GitSync installation flow.
 */
export function InstallationStep(props: GitSyncInstallationStepProps) {
    const headingId = React.useId();

    return (
        <IntegrationStepWrapper
            title="Select your Git provider"
            headingId={headingId}
            description="Choose the git provider where your content is hosted."
        >
            <ErrorBoundary
                fallback={
                    <Alert
                        kind="danger"
                        title="Error while fetching Git Sync integration"
                        details="Something went wrong while loading the Git Sync integrations available. Please try reloading the app."
                    />
                }
            >
                <React.Suspense
                    fallback={
                        <div className="flex flex-col gap-4">
                            <CardSkeleton />
                            <CardSkeleton />
                        </div>
                    }
                >
                    <GitProviderPicker {...props} headingId={headingId} />
                </React.Suspense>
            </ErrorBoundary>
        </IntegrationStepWrapper>
    );
}

/**
 * Fetch and display cards of GitSync integrations.
 */
function GitProviderPicker(props: GitSyncInstallationStepProps & { headingId: string }) {
    const { data } = useSuspenseQuery(
        apiQuery(
            'listOrganizationIntegrations',
            { organizationId: props.organization.id, category: api.IntegrationCategory.Gitsync },
            // Refactor: handle the error case in a react boundary
            { nullOnError: true }
        )
    );
    const gitSyncIntegrations = data?.items || [];

    const [pickedIntegration, setPickedIntegration] = React.useState<api.Integration | null>(null);
    const installatonState = useLoadingState();

    return (
        <Step>
            <RadioGroup
                onChange={(value) => {
                    const integration = gitSyncIntegrations.find(({ name }) => name === value);
                    assertIsDefined(integration, `Picked integration ${value} not found`);

                    setPickedIntegration(integration);
                }}
                isDisabled={installatonState[0]}
                aria-labelledby={props.headingId}
            >
                {gitSyncIntegrations.map((integration) => (
                    <GitSyncProviderCard
                        {...props}
                        key={integration.name}
                        integration={integration}
                    />
                ))}
            </RadioGroup>
            <StepControls>
                <div className="flex w-full justify-end">
                    {pickedIntegration ? (
                        <GitProvidersSubmitButton {...props} integration={pickedIntegration} />
                    ) : (
                        <StepControlsSubmitButton disabled />
                    )}
                </div>
            </StepControls>
        </Step>
    );
}

/**
 * Submit button for the GitSync provider selection step.
 *
 * This button will install the selected integration in the organization (if needed) and space.
 */
function GitProvidersSubmitButton(props: {
    integration: api.Integration;
    space: api.Space;
    onNext: (integration: api.Integration) => void;
}) {
    const { integration, space, onNext } = props;
    const { installIntegrationInSpace } = useIntegrationInstallAction({
        integration,
        organizationId: space.organization,
    });

    return (
        <StepControlsSubmitButton
            onGoNext={async () => {
                await installIntegrationInSpace({ space: space.id, suppressToast: true });

                onNext(integration);
            }}
        />
    );
}
/**
 * Card displaying a GitSync integration details.
 */
function GitSyncProviderCard(
    props: GitSyncInstallationStepProps & {
        integration: api.Integration;
    }
) {
    const { integration, space, onNext } = props;

    const { isInstalledInSpace, canInstallInSpace } = useIntegrationStatus({
        integrationName: integration.name,
        organization: props.organization,
        space: space,
    });

    React.useEffect(() => {
        // Skip step if already installed in space, edge case, where the integration is installed but not configured.
        if (isInstalledInSpace) {
            onNext(integration);
        }
    }, [integration, isInstalledInSpace, onNext]);

    return (
        <CardRadio
            value={integration.name}
            isDisabled={!canInstallInSpace || isInstalledInSpace}
            className={'flex items-center gap-4 rounded-md pr-4'}
        >
            <CardImageLayout>
                <CardImage>
                    <IconTile size={48} className="bg-white">
                        <IntegrationIcon integration={integration} className="size-full" />
                    </IconTile>
                </CardImage>
                <CardBody>
                    <Heading>{integration.title}</Heading>
                    <Description>{integration.description}</Description>
                </CardBody>
            </CardImageLayout>
            <CardCheck className="ml-auto" />
        </CardRadio>
    );
}

/**
 * A utility hook to get the status of an integration in an organization and space.
 */
function useIntegrationStatus(props: {
    integrationName: string;
    organization: api.Organization;
    space: api.Space;
}) {
    const { integrationName, organization, space } = props;

    const installationId = getIntegrationInstallationKey(integrationName, {
        organization: organization.id,
    });

    const [{ data: spaceInstallation }] = useSuspenseQueries({
        queries: [
            apiQuery(
                'getIntegrationSpaceInstallation',
                {
                    integrationName,
                    installationId,
                    spaceId: space.id,
                    extended: true,
                },
                { nullOnError: true }
            ),
        ],
    });

    assertIsDefined(integration, `Integration ${integrationName} could not be found`);

    return {
        canInstallInOrg: organization.permissions.installIntegration,
        canInstallInSpace: space.permissions.installIntegration,
        isInstalledInSpace: Boolean(spaceInstallation),
    };
}

/**
 * A utility hook to install an integration in a space.
 */
function useIntegrationInstallAction(props: {
    integration: api.Integration;
    organizationId: string;
}) {
    const { integration, organizationId } = props;
    const { data: orgInstallation } = useSuspenseQuery(
        apiQuery(
            'getIntegrationInstallationById',
            {
                integrationName: integration.name,
                installationId: getIntegrationInstallationKey(integration.name, {
                    organization: organizationId,
                }),
            },
            { nullOnError: true }
        )
    );

    const [installIntegrationInSpace] = useInstallIntegration(
        integration,
        { organization: organizationId },
        Boolean(orgInstallation)
    );

    return {
        installIntegrationInSpace,
    };
}
